import { ArrowLeft, CheckCircle2, Clock3, Download, ExternalLink, FileText, ShieldCheck, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { apiBlob, apiRequest, describeApiError } from "../../lib/api";
import { formatTimestamp } from "../../lib/format";
import { formatBytes, formatMxn, formatPhone } from "../../lib/loans";
import { useInvalidateAll, usePartnerApplication } from "../../lib/queries";
import type { PartnerLoanApplication } from "../../types";
import { AuditList, ConfirmButton, Feedback, useAction } from "../admin/shared";
import { LoanStatusTag } from "../loan/LoanShared";

/** One application, everything the lender needs to decide, and the decision itself. */
export function PartnerApplicationPage({ base }: { base: string }) {
  const { id } = useParams();
  const detail = usePartnerApplication(id);
  usePageHeader(detail.data ? `Solicitud ${detail.data.application.folio}` : "Solicitud", detail.data?.application.contact.fullName);
  if (detail.isLoading) return <div className="page-state"><Spinner label="Cargando" /></div>;
  if (detail.isError || !detail.data) {
    return <div className="page-state error"><strong>No encontramos esta solicitud</strong><Link className="secondary-button" to={`${base}/solicitudes`}>Volver</Link></div>;
  }
  const { application, audit } = detail.data;
  const final = application.status === "approved" || application.status === "rejected";
  return (
    <div className="page-container">
      <Link to={`${base}/solicitudes`} className="back-button"><ArrowLeft size={16} aria-hidden="true" /> Todas las solicitudes</Link>
      <div className="admin-two-col">
        <div className="admin-stack">
          <section className="card partner-summary">
            <div className="loan-card-head">
              <div><span className="rail-title">Folio</span><strong className="loan-folio" translate="no">{application.folio}</strong></div>
              <LoanStatusTag status={application.status} audience="partner" />
            </div>
            <div className="partner-amount-row">
              <div><span>Monto solicitado</span><strong translate="no">{formatMxn(application.amount)}</strong></div>
              <div><span>Plazo preferido</span><strong>{application.termMonths} meses</strong></div>
              <div><span>Pago mensual de referencia</span><strong translate="no">{application.estimatedMonthlyPayment ? formatMxn(application.estimatedMonthlyPayment) : "—"}</strong><small>tasa {application.annualRatePercent ?? "—"}% anual, informativa</small></div>
            </div>
            <dl className="review-list loan-review">
              <div><dt>CURP</dt><dd translate="no" className="mono">{application.curp}</dd></div>
              <div><dt>Nombre (según solicitud)</dt><dd>{application.contact.fullName}</dd></div>
              <div><dt>Correo de contacto</dt><dd>{application.contact.email}</dd></div>
              <div><dt>Teléfono</dt><dd translate="no">{formatPhone(application.contact.phone)}</dd></div>
              <div><dt>Enviada</dt><dd>{formatTimestamp(application.submittedAt)}</dd></div>
              {application.reviewedAt && <div><dt>En revisión desde</dt><dd>{formatTimestamp(application.reviewedAt)}{application.reviewedBy ? ` · ${application.reviewedBy}` : ""}</dd></div>}
              {application.decidedAt && <div><dt>Decisión</dt><dd>{formatTimestamp(application.decidedAt)}{application.decidedBy ? ` · ${application.decidedBy}` : ""}</dd></div>}
            </dl>
          </section>

          <DocumentCard application={application} />

          <section className="card">
            <div className="section-heading small"><div><span className="page-eyebrow">Evidencia</span><h2>Autorización de historial crediticio</h2></div><span className="eligibility-row"><ShieldCheck size={14} aria-hidden="true" /> Aceptada</span></div>
            <dl className="review-list loan-review">
              <div><dt>Aceptada el</dt><dd>{formatTimestamp(application.authorization.acceptedAt)}</dd></div>
              <div><dt>Huella del texto (SHA-256)</dt><dd className="mono small" translate="no">{application.authorization.textSha256}</dd></div>
              <div><dt>Registro técnico</dt><dd>{application.authorization.ipRecorded ? "Dirección IP" : "—"}{application.authorization.userAgentRecorded ? " y navegador" : ""} conservados por Aventuria</dd></div>
            </dl>
            <details className="partner-auth-text">
              <summary>Ver el texto exacto que aceptó el solicitante</summary>
              {application.authorization.text.split(/\n\s*\n/).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
            </details>
          </section>
        </div>

        <div className="admin-stack">
          <DecisionCard application={application} final={final} />
          <section className="card">
            <span className="rail-title">Cuenta Aventuria del solicitante</span>
            {application.applicant ? (
              <dl className="review-list loan-review">
                <div><dt>Nombre en la cuenta</dt><dd>{application.applicant.fullName}</dd></div>
                <div><dt>Correo de la cuenta</dt><dd>{application.applicant.email}</dd></div>
                <div><dt>Teléfono en perfil</dt><dd translate="no">{formatPhone(application.applicant.phone)}</dd></div>
                <div><dt>Verificación de identidad</dt><dd><span className={`tag kyc-${application.applicant.kycStatus}`}>{application.applicant.kycStatus}</span></dd></div>
                <div><dt>Cliente desde</dt><dd>{formatTimestamp(application.applicant.memberSince)}</dd></div>
              </dl>
            ) : <p className="rail-empty">La cuenta ya no existe.</p>}
          </section>
          <section className="card">
            <span className="rail-title">Bitácora de la solicitud</span>
            <AuditList events={audit} empty="Sin actividad registrada." />
          </section>
        </div>
      </div>
    </div>
  );
}

/**
 * The PDF is fetched with the session token (a plain link cannot carry it) and offered as an
 * object URL: open in a new tab, or download. The API records every read in the audit trail.
 */
function DocumentCard({ application }: { application: PartnerLoanApplication }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string[] | null>(null);
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const blob = await apiBlob(`/api/v1/partner/applications/${application.id}/document`);
      setBlobUrl(URL.createObjectURL(blob));
    } catch (cause) {
      setError(describeApiError(cause, "No pudimos abrir el documento."));
    } finally { setLoading(false); }
  };

  const document = application.document;
  const fileName = document?.originalName ?? `constancia-${application.folio}.pdf`;
  return (
    <section className="card">
      <div className="section-heading small"><div><span className="page-eyebrow">Documento</span><h2>Constancia de Situación Fiscal</h2></div></div>
      {document ? (
        <>
          <div className="loan-file ok">
            <span className="loan-file-icon"><FileText size={18} aria-hidden="true" /></span>
            <div>
              <strong translate="no">{fileName}</strong>
              <span>PDF · {formatBytes(document.bytes)} · subido {formatTimestamp(document.createdAt)}</span>
              <span className="mono small" translate="no">SHA-256 {document.sha256}</span>
            </div>
          </div>
          <div className="inline-actions loan-file-actions">
            {blobUrl ? (
              <>
                <a className="primary-button solid small" href={blobUrl} target="_blank" rel="noopener"><ExternalLink size={14} aria-hidden="true" /> Abrir en una pestaña</a>
                <a className="secondary-button small" href={blobUrl} download={fileName}><Download size={14} aria-hidden="true" /> Descargar</a>
              </>
            ) : (
              <button type="button" className="primary-button solid small" onClick={() => void load()} disabled={loading}>{loading ? <Spinner /> : <FileText size={14} aria-hidden="true" />} Ver constancia</button>
            )}
          </div>
          <Feedback error={error} notice={null} />
        </>
      ) : <p className="rail-empty">El documento ya no está disponible.</p>}
    </section>
  );
}

function DecisionCard({ application, final }: { application: PartnerLoanApplication; final: boolean }) {
  const invalidate = useInvalidateAll();
  const action = useAction();
  const [note, setNote] = useState(application.decisionNote ?? "");
  useEffect(() => setNote(application.decisionNote ?? ""), [application.decisionNote]);

  const decide = (status: "in_review" | "approved" | "rejected") => async () => {
    await apiRequest(`/api/v1/partner/applications/${application.id}/decision`, {
      method: "POST", body: JSON.stringify({ status, ...(note.trim() ? { note: note.trim() } : {}) })
    });
    await invalidate();
  };

  if (final) {
    return (
      <section className={`card partner-decision ${application.status}`}>
        <span className="rail-title">Decisión registrada</span>
        <div className="partner-decision-head">
          {application.status === "approved" ? <CheckCircle2 size={26} aria-hidden="true" /> : <XCircle size={26} aria-hidden="true" />}
          <div>
            <strong>{application.status === "approved" ? "Solicitud aprobada" : "Solicitud rechazada"}</strong>
            <span>{application.decidedAt ? formatTimestamp(application.decidedAt) : ""}{application.decidedBy ? ` · ${application.decidedBy}` : ""}</span>
          </div>
        </div>
        {application.decisionNote && <blockquote className="loan-decision-note"><span>Nota</span>{application.decisionNote}</blockquote>}
        <p className="field-hint">El solicitante recibió un correo con la decisión. Las condiciones finales del crédito las comunica Peso a Peso directamente.</p>
      </section>
    );
  }

  return (
    <section className="card partner-decision">
      <span className="rail-title">Decisión</span>
      <h2 className="partner-decision-title">{application.status === "submitted" ? "Solicitud nueva" : "En revisión"}</h2>
      <p className="field-hint">Aventuria avisará al solicitante por correo en cuanto registres una decisión final. La nota es opcional y se incluye en ese correo.</p>
      <label className="field-block" htmlFor="decision-note">
        <span className="field-label">Nota para el solicitante (opcional)</span>
        <textarea id="decision-note" className="standalone-input textarea" rows={3} maxLength={1000} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Ej. Un ejecutivo te contactará en 2 días hábiles." />
      </label>
      <Feedback error={action.error} notice={action.notice} />
      <div className="danger-actions partner-actions">
        {application.status === "submitted" && (
          <button type="button" className="secondary-button" disabled={action.busy !== null} onClick={() => void action.run("review", async () => { await decide("in_review")(); return "Marcada como en revisión."; })}>
            {action.busy === "review" ? <Spinner /> : <Clock3 size={15} aria-hidden="true" />} Tomar en revisión
          </button>
        )}
        <ConfirmButton
          label="Aprobar" icon={<CheckCircle2 size={15} aria-hidden="true" />} title="Aprobar la solicitud"
          message={<p>Se registrará la aprobación del folio <strong>{application.folio}</strong> por <strong>{formatMxn(application.amount)}</strong> y se avisará al solicitante. Esta decisión no se puede cambiar después.</p>}
          confirmLabel="Aprobar solicitud" onConfirm={decide("approved")}
        />
        <ConfirmButton
          label="Rechazar" danger icon={<XCircle size={15} aria-hidden="true" />} title="Rechazar la solicitud"
          message={<p>Se registrará el rechazo del folio <strong>{application.folio}</strong> y se avisará al solicitante. Esta decisión no se puede cambiar después.</p>}
          confirmLabel="Rechazar solicitud" onConfirm={decide("rejected")}
        />
      </div>
    </section>
  );
}
